import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import {
  Sparkles,
  CreditCard,
  UserCheck,
  Wallet,
  Banknote,
  AlertTriangle,
  Loader2,
  Inbox,
  Check,
} from "lucide-react";

const ICONS = {
  welcome: { icon: Sparkles, tone: "lime" },
  subscription_activated: { icon: CreditCard, tone: "lime" },
  partner_approved: { icon: UserCheck, tone: "lime" },
  commission_earned: { icon: Wallet, tone: "lime" },
  payout_requested: { icon: Banknote, tone: "blue" },
  payout_paid: { icon: Banknote, tone: "lime" },
  quota_warning: { icon: AlertTriangle, tone: "amber" },
};

const ACTION_LINKS = {
  welcome: { to: "/dashboard", labelKey: "activity.action_dashboard" },
  subscription_activated: { to: "/dashboard", labelKey: "activity.action_dashboard" },
  partner_approved: { to: "/partner/kit", labelKey: "activity.action_kit" },
  commission_earned: { to: "/partner", labelKey: "activity.action_partner" },
  payout_requested: { to: "/partner", labelKey: "activity.action_partner" },
  payout_paid: { to: "/partner", labelKey: "activity.action_partner" },
  quota_warning: { to: "/pricing", labelKey: "activity.action_upgrade" },
};

export default function Activity() {
  const { t, locale } = useI18n();
  const { refresh } = useAuth();
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [unread, setUnread] = useState(0);

  const load = async (c = null, append = false) => {
    const setter = append ? setLoadingMore : setLoading;
    setter(true);
    try {
      const res = await api.get("/activity/events", { params: { limit: 30, cursor: c || undefined } });
      setEvents((prev) => (append ? [...prev, ...res.data.events] : res.data.events));
      setCursor(res.data.next_cursor);
      setUnread(res.data.unread_count || 0);
    } finally {
      setter(false);
    }
  };

  useImperativeLoad(() => { load(); }, []);

  const markAllRead = async () => {
    await api.post("/activity/mark-read");
    setEvents((evts) => evts.map((e) => ({ ...e, read: true })));
    setUnread(0);
    refresh().catch(() => {});
  };

  const grouped = groupByDay(events, locale);

  return (
    <div className="px-8 py-10 lg:px-12" data-testid="activity-page">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#9ACD32]">{t("activity.pretitle")}</p>
          <h1 className="mt-1 font-display text-4xl font-extrabold tracking-tight text-white">{t("activity.title")}</h1>
          <p className="mt-2 max-w-2xl text-slate-300">{t("activity.subtitle")}</p>
        </div>
        {unread > 0 && (
          <Button onClick={markAllRead} variant="outline" className="rounded-full border-slate-700 text-slate-200" data-testid="activity-mark-read-btn">
            <Check className="mr-2 h-4 w-4" />
            {t("activity.mark_all_read")} ({unread})
          </Button>
        )}
      </header>

      {loading ? (
        <div className="flex h-64 items-center justify-center text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : events.length === 0 ? (
        <Card className="border-slate-800 bg-[#0A0C1F] p-10 text-center" data-testid="activity-empty">
          <Inbox className="mx-auto h-10 w-10 text-slate-600" />
          <p className="mt-4 text-base font-bold text-white">{t("activity.empty_title")}</p>
          <p className="mt-1 text-sm text-slate-400">{t("activity.empty_desc")}</p>
        </Card>
      ) : (
        <div className="relative space-y-10">
          {grouped.map((group) => (
            <section key={group.label}>
              <h2 className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-slate-500">{group.label}</h2>
              <div className="relative ml-1">
                <div className="absolute bottom-2 left-[15px] top-2 w-px bg-slate-800" aria-hidden />
                <ul className="space-y-4">
                  {group.events.map((ev) => (
                    <EventRow key={ev.event_id} event={ev} t={t} locale={locale} />
                  ))}
                </ul>
              </div>
            </section>
          ))}

          {cursor && (
            <div className="pt-4 text-center">
              <Button
                onClick={() => load(cursor, true)}
                disabled={loadingMore}
                variant="outline"
                className="rounded-full border-slate-700 text-slate-200"
                data-testid="activity-load-more-btn"
              >
                {loadingMore ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t("activity.load_more")}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EventRow({ event, t, locale }) {
  const meta = ICONS[event.type] || { icon: Sparkles, tone: "slate" };
  const Icon = meta.icon;
  const action = ACTION_LINKS[event.type];

  const toneRing = {
    lime: "bg-[#9ACD32]/15 ring-[#9ACD32]/40 text-[#9ACD32]",
    blue: "bg-[#0B0E5C]/40 ring-[#7AAA20]/30 text-[#9ACD32]",
    amber: "bg-amber-500/15 ring-amber-500/40 text-amber-300",
    slate: "bg-slate-700/30 ring-slate-600 text-slate-300",
  }[meta.tone];

  return (
    <li className="relative pl-12" data-testid={`activity-event-${event.type}`}>
      <span className={`absolute left-0 top-1 flex h-8 w-8 items-center justify-center rounded-full ring-2 ${toneRing}`}>
        <Icon className="h-4 w-4" />
      </span>
      <Card className={`border-slate-800 bg-[#0A0C1F] p-5 ${event.read ? "" : "ring-1 ring-[#9ACD32]/30"}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-base font-bold text-white">{formatTitle(event, t)}</h3>
            <p className="mt-1 text-sm text-slate-400">{formatDescription(event, t, locale)}</p>
          </div>
          <span className="text-xs text-slate-500">{formatTime(event.created_at, locale)}</span>
        </div>
        {action && (
          <div className="mt-4">
            <Link to={action.to}>
              <Button size="sm" variant="outline" className="rounded-full border-[#9ACD32]/40 bg-[#9ACD32]/5 text-xs font-bold text-[#9ACD32] hover:bg-[#9ACD32]/10" data-testid={`activity-action-${event.type}`}>
                {t(action.labelKey)}
              </Button>
            </Link>
          </div>
        )}
      </Card>
    </li>
  );
}

function formatTitle(event, t) {
  return t(`activity.event.${event.type}.title`, { default: event.type });
}

function formatDescription(event, t, locale) {
  const d = event.data || {};
  switch (event.type) {
    case "subscription_activated":
      return t("activity.event.subscription_activated.desc", {
        plan: (d.plan_id || "").toUpperCase(),
        amount: formatEur(d.amount_eur, locale),
        default: `${(d.plan_id || "").toUpperCase()} · ${formatEur(d.amount_eur, locale)}`,
      });
    case "commission_earned":
      return t("activity.event.commission_earned.desc", {
        amount: formatEur(d.commission_eur, locale),
        plan: (d.plan_id || "").toUpperCase(),
        default: `${formatEur(d.commission_eur, locale)} · ${(d.plan_id || "").toUpperCase()}`,
      });
    case "payout_requested":
      return t("activity.event.payout_requested.desc", {
        amount: formatEur(d.amount_eur, locale),
        count: d.count || 0,
        default: `${formatEur(d.amount_eur, locale)} · ${d.count || 0}`,
      });
    case "payout_paid":
      return t("activity.event.payout_paid.desc", {
        amount: formatEur(d.amount_eur, locale),
        default: formatEur(d.amount_eur, locale),
      });
    case "quota_warning": {
      const pct = d.limit ? Math.round((100 * d.used) / d.limit) : 0;
      return t("activity.event.quota_warning.desc", {
        pct,
        resource: d.resource,
        used: d.used,
        limit: d.limit,
        default: `${pct}% ${d.resource}`,
      });
    }
    default:
      return t(`activity.event.${event.type}.desc`, { default: "" });
  }
}

function formatEur(n, locale) {
  if (n == null) return "€0";
  const num = Number(n);
  if (locale === "it" || locale === "pl") {
    return `€${num.toFixed(2).replace(".", ",")}`;
  }
  return `€${num.toFixed(2)}`;
}

function formatTime(iso, locale) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(locale === "en" ? "en-US" : locale, { hour: "2-digit", minute: "2-digit" });
  } catch (e) { return ""; }
}

function groupByDay(events, locale) {
  const groups = new Map();
  for (const e of events) {
    const d = new Date(e.created_at);
    const key = d.toISOString().slice(0, 10);
    if (!groups.has(key)) {
      const label = formatDayLabel(d, locale);
      groups.set(key, { key, label, events: [] });
    }
    groups.get(key).events.push(e);
  }
  return Array.from(groups.values());
}

function formatDayLabel(d, locale) {
  const today = new Date();
  const yesterday = new Date(Date.now() - 86400000);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  const i18nLocale = locale === "en" ? "en-US" : locale;
  if (sameDay(d, today)) return ({ it: "Oggi", en: "Today", pl: "Dzisiaj" })[locale] || "Today";
  if (sameDay(d, yesterday)) return ({ it: "Ieri", en: "Yesterday", pl: "Wczoraj" })[locale] || "Yesterday";
  return d.toLocaleDateString(i18nLocale, { day: "numeric", month: "long", year: "numeric" });
}
